import type { OtuUnassignedSample } from '../../Generated/OtuUnassignedSample.ts'
import type { OrderAlert } from './orderAlerts.ts'

/**
 * The share of a sample's or scope's reads in OTUs without a valid BLAST hit above which it is flagged. One flagged
 * sample puts a warning on the whole order: on prod (2026-11) 10 % did so for a third of the main lab's recent orders,
 * 30 % for a fifth.
 */
export const UNASSIGNED_READS_WARN_PERCENT = 30

/** [rawReads] as a percentage of all reads; 0 without reads. */
export function unassignedPercent(rawReads: number, assignedRawReads: number): number {
  const total = rawReads + assignedRawReads
  return total === 0 ? 0 : (100 * rawReads) / total
}

const samplePercent = (s: OtuUnassignedSample): number => unassignedPercent(s.rawReads, s.assignedRawReads)

/** The samples at or above the threshold, worst first. Negative controls are left out: their few reads make any share meaningless. */
export function flaggedUnassignedSamples(samples: readonly OtuUnassignedSample[] | undefined): OtuUnassignedSample[] {
  return (samples ?? [])
    .filter((s) => s.itemType !== 'NEGATIVE_CONTROL' && samplePercent(s) >= UNASSIGNED_READS_WARN_PERCENT)
    .sort((a, b) => samplePercent(b) - samplePercent(a))
}

/** "S1 (34.2 %)" */
export const flaggedSampleLabel = (s: OtuUnassignedSample): string => `${s.sampleName ?? s.sampleId} (${samplePercent(s).toFixed(1)} %)`

/**
 * The warning for a scope or order with [rawReads] unassigned of all its reads and the given per-sample breakdown,
 * null when nothing crosses the threshold.
 */
export function unassignedReadsWarning(
  rawReads: number,
  assignedRawReads: number,
  samples: readonly OtuUnassignedSample[] | undefined
): string | null {
  const flagged = flaggedUnassignedSamples(samples)
  const overall = unassignedPercent(rawReads, assignedRawReads) >= UNASSIGNED_READS_WARN_PERCENT
  if (!overall && flagged.length === 0) return null
  if (flagged.length === 0) return `${unassignedPercent(rawReads, assignedRawReads).toFixed(1)} % of reads have no valid BLAST hit`
  const noun = flagged.length === 1 ? 'sample has' : 'samples have'
  return `${flagged.length} ${noun} ${UNASSIGNED_READS_WARN_PERCENT} % or more of reads without a valid BLAST hit`
}

/**
 * The order-page alert for an order's flagged samples, empty when none is flagged. Computed on every view, so it is
 * never resolved and every order member sees it.
 */
export function unassignedAlerts(samples: readonly OtuUnassignedSample[] | undefined): OrderAlert[] {
  const flagged = flaggedUnassignedSamples(samples)
  if (flagged.length === 0) return []
  const noun = flagged.length === 1 ? 'sample' : 'samples'
  return [
    {
      id: 'computed:unassigned-blast',
      title: `${flagged.length} ${noun} with many reads without a valid BLAST hit`,
      body:
        `In these ${noun}, ${UNASSIGNED_READS_WARN_PERCENT} % or more of the reads belong to OTUs without a valid BLAST hit ` +
        `and so carry no taxonomic assignment:\n` +
        flagged.map(flaggedSampleLabel).join('\n'),
      audience: null,
      resolved: false
    }
  ]
}
